/**
 * Month density calendar — tap a day to jump to day view.
 */
import React, { useMemo } from 'react';
import { RefreshControl, ScrollView, StyleSheet, Text, TouchableOpacity, View } from 'react-native';
import { agenda, agendaFonts } from '../domain/agendaTheme';
import type { Appointment, AppointmentStatus } from '../domain/types';
import { monthDaySummary } from '../domain/dayBoardLayout';
import { PaperSheet } from '../../shared/ticket/PaperSheet';

function startOfLocalDay(d: Date): Date {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
}

function isSameLocalDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const DOW = ['L', 'M', 'X', 'J', 'V', 'S', 'D'];

/** Fixed dot order (proto month cells): one dot per status present, cancelled excluded. */
const STATUS_DOT_ORDER: ReadonlyArray<AppointmentStatus> = [
  'requested',
  'confirmed',
  'completed',
  'no_show',
];

const STATUS_DOT_COLOR: Record<AppointmentStatus, string> = {
  requested: agenda.requested.border,
  confirmed: agenda.confirmed.border,
  completed: agenda.completed.border,
  cancelled: agenda.cancelled.border,
  no_show: agenda.noShow.border,
};

const STATUS_LABELS: Record<AppointmentStatus, string> = {
  requested: 'solicitados',
  confirmed: 'confirmados',
  completed: 'completados',
  cancelled: 'cancelados',
  no_show: 'no vinieron',
};

type Props = {
  anchorDay: Date;
  /** Desktop: taller cells that use available width. */
  isWide?: boolean;
  /** Agendas counted (empty/undefined = all). */
  resourceIds?: ReadonlyArray<string>;
  appointments: Appointment[];
  onSelectDay: (day: Date) => void;
  refreshing?: boolean;
  onRefresh?: () => void;
};

export function AppointmentsMonthBoard(props: Props) {
  const year = props.anchorDay.getFullYear();
  const month = props.anchorDay.getMonth();
  const today = startOfLocalDay(new Date());
  const isWide = props.isWide === true;

  const cells = useMemo(() => {
    const first = new Date(year, month, 1);
    const firstDow = first.getDay(); // 0 Sun
    const mondayBased = firstDow === 0 ? 6 : firstDow - 1;
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const out: Array<Date | null> = [];
    for (let i = 0; i < mondayBased; i++) out.push(null);
    for (let d = 1; d <= daysInMonth; d++) out.push(new Date(year, month, d));
    while (out.length % 7 !== 0) out.push(null);
    return out;
  }, [year, month]);

  const summaryByDay = useMemo(
    () => monthDaySummary(props.appointments, props.resourceIds),
    [props.appointments, props.resourceIds],
  );

  return (
    <PaperSheet style={styles.paper}>
    <ScrollView
      style={styles.root}
      contentContainerStyle={styles.rootContent}
      refreshControl={
        props.onRefresh ? (
          <RefreshControl
            refreshing={Boolean(props.refreshing)}
            onRefresh={props.onRefresh}
                colors={[agenda.brand]}
                tintColor={agenda.brand}
          />
        ) : undefined
      }
    >
      <View style={styles.dowRow}>
        {DOW.map((d) => (
          <Text key={d} style={styles.dow}>
            {d}
          </Text>
        ))}
      </View>
      <View style={styles.grid}>
        {cells.map((day, i) => {
          if (!day) {
            return (
              <View
                key={`e-${i}`}
                style={[styles.cell, isWide ? styles.cellWide : styles.cellMobile]}
              />
            );
          }
          const summary = summaryByDay.get(day.toDateString());
          const count = summary?.count ?? 0;
          const isToday = isSameLocalDay(day, today);
          const isAnchor = isSameLocalDay(day, props.anchorDay);
          const dayLong = day.toLocaleDateString('es-AR', {
            weekday: 'long',
            day: 'numeric',
            month: 'long',
          });
          const statusParts = summary
            ? STATUS_DOT_ORDER.filter((s) => summary.statuses.has(s)).map(
                (s) => STATUS_LABELS[s],
              )
            : [];
          return (
            <TouchableOpacity
              key={day.toISOString()}
              style={[
                styles.cell,
                isWide ? styles.cellWide : styles.cellMobile,
                isAnchor && styles.cellAnchor,
                isToday && styles.cellToday,
              ]}
              onPress={() => props.onSelectDay(day)}
              activeOpacity={0.85}
              accessibilityRole="button"
              accessibilityLabel={`${dayLong}${isToday ? ', hoy' : ''}, ${
                count === 0 ? 'sin turnos' : `${count} ${count === 1 ? 'turno' : 'turnos'}`
              }${statusParts.length > 0 ? ` (${statusParts.join(', ')})` : ''} — ver día`}
            >
              <Text style={[styles.dayNum, isToday && styles.dayNumToday]}>{day.getDate()}</Text>
              {count > 0 ? (
                <>
                  <View style={styles.dotsRow}>
                    {STATUS_DOT_ORDER.filter((s) => summary!.statuses.has(s)).map((s) => (
                      <View
                        key={s}
                        style={[styles.statusDot, { backgroundColor: STATUS_DOT_COLOR[s] }]}
                      />
                    ))}
                  </View>
                  <Text style={styles.countText} numberOfLines={1}>
                    {isWide && summary!.noShowCount > 0 ? (
                      <>
                        {summary!.count} ·{' '}
                        <Text style={styles.noShowText}>{summary!.noShowCount} no-show</Text>
                      </>
                    ) : (
                      `${count} turno${count === 1 ? '' : 's'}`
                    )}
                  </Text>
                </>
              ) : (
                <Text style={styles.dash}>·</Text>
              )}
            </TouchableOpacity>
          );
        })}
      </View>
      {/* Los dots de las celdas no son auto-explicativos — leyenda de estados. */}
      <View style={styles.legend} accessibilityLabel="Leyenda de estados">
        {STATUS_DOT_ORDER.map((s) => (
          <View key={s} style={styles.legendItem}>
            <View style={[styles.statusDot, { backgroundColor: STATUS_DOT_COLOR[s] }]} />
            <Text style={styles.legendText}>{STATUS_LABELS[s]}</Text>
          </View>
        ))}
      </View>
    </ScrollView>
    </PaperSheet>
  );
}

const styles = StyleSheet.create({
  paper: { flex: 1, minHeight: 0 },
  root: { flex: 1, backgroundColor: agenda.surface },
  rootContent: { padding: 14, flexGrow: 1 },
  dowRow: { flexDirection: 'row', marginBottom: 6 },
  dow: {
    flex: 1,
    textAlign: 'center',
    fontSize: 11,
    fontWeight: '700',
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
  grid: { flexDirection: 'row', flexWrap: 'wrap' },
  cell: {
    width: `${100 / 7}%`,
    aspectRatio: 1,
    padding: 4,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
  },
  cellMobile: { maxHeight: 72 },
  cellWide: { maxHeight: 112 },
  cellAnchor: { backgroundColor: agenda.chooseBg, borderWidth: 1, borderColor: agenda.brand },
  cellToday: { borderWidth: 2, borderColor: agenda.brand },
  dayNum: { fontSize: 14, fontWeight: '700', color: agenda.ink, fontFamily: agendaFonts.sans },
  dayNumToday: { color: agenda.brand },
  dotsRow: { flexDirection: 'row', gap: 3, marginTop: 5, flexWrap: 'wrap', justifyContent: 'center' },
  statusDot: { width: 6, height: 6, borderRadius: 3 },
  countText: {
    marginTop: 2,
    fontSize: 11,
    fontWeight: '600',
    color: agenda.ink2,
    textAlign: 'center',
    fontFamily: agendaFonts.sans,
  },
  noShowText: { color: agenda.noShow.border, fontWeight: '700', fontFamily: agendaFonts.sans },
  dash: { marginTop: 4, fontSize: 12, color: agenda.border },
  legend: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    justifyContent: 'center',
    marginTop: 12,
  },
  legendItem: { flexDirection: 'row', alignItems: 'center', gap: 4 },
  legendText: {
    fontSize: 11,
    color: agenda.ink2,
    fontFamily: agendaFonts.sans,
  },
});
